import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { getLatestAppstoreOrder, Cafe24AppstoreOrder } from '@/api/cafe24Api';

/**
 * 카페24 앱스토어 결제(Appstore Orders) return_url 착지 화면.
 * CAFE24_CMS_INTEGRATION_DESIGN.md §10 참고.
 *
 * <p>카페24 결제창에서 결제를 마치면 여기로 돌아온다. 다만 이 리다이렉트는
 * "결제 완료"의 신뢰 가능한 증거가 아니다(사용자가 브라우저를 그냥 닫아버리면
 * 안 옴) - 실제 확정은 웹훅/재조회 안전망이 한다. 그래서 여기서는 최근 주문
 * 상태를 조회해 보여주고, 아직 PAID가 아니면 "확인 중" 안내와 새로고침
 * 버튼을 제공한다(재조회 스케줄러가 최대 10분 주기).</p>
 */
export const Cafe24AppstorePaymentResult: React.FC = () => {
  const navigate = useNavigate();
  const [order, setOrder] = useState<Cafe24AppstoreOrder | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);

  const checkStatus = useCallback(async () => {
    setChecking(true);
    setError(null);
    try {
      const latest = await getLatestAppstoreOrder();
      setOrder(latest);
    } catch {
      setError('결제 상태를 확인할 수 없습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center px-4 max-w-sm">
        {checking && (
          <>
            <Spinner size="2xl" className="mx-auto mb-4 text-primary" />
            <p className="text-gray-600">결제 상태를 확인하는 중...</p>
          </>
        )}
        {!checking && error && (
          <>
            <p className="text-red-600 mb-6">{error}</p>
            <Button onClick={checkStatus}>다시 확인</Button>
          </>
        )}
        {!checking && !error && order?.status === 'PAID' && (
          <>
            <p className="text-lg font-semibold text-gray-900 mb-2">결제가 완료됐습니다.</p>
            <p className="text-gray-600 mb-6">{order.orderName} 결제가 정상적으로 확인됐습니다.</p>
            <Button onClick={() => navigate('/dashboard')}>대시보드로 이동</Button>
          </>
        )}
        {!checking && !error && order && order.status !== 'PAID' && (
          <>
            <p className="text-lg font-semibold text-gray-900 mb-2">결제 확인 중입니다.</p>
            <p className="text-gray-600 mb-6">
              카페24에서 결제를 완료하셨다면, 반영까지 최대 10분 정도 걸릴 수 있습니다.
            </p>
            <div className="flex gap-2 justify-center">
              <Button onClick={checkStatus}>새로고침</Button>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>대시보드로 이동</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
